import {
  Disclosure,
  PageHeader,
  Select,
  buttonVariants,
  cn,
} from "@safeplate/ui";
import { ArrowRight } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { Link, Navigate, useNavigate, useSearch } from "@tanstack/react-router";

import { EmptyState } from "../components/states.js";
import { PlanWorkspace } from "../plan/plan-workspace.js";
import { useShell } from "../shell/shell-context.js";
import { AssignChecks } from "./assign-checks.js";
import { LocationPanel } from "./location-panel.js";
import { RegistrationPanel } from "./registration-panel.js";
import { ResourcePanel } from "./resource-panel.js";
import { RuleAnswersPanel } from "./rule-answers.js";
import {
  areaResource,
  assetResource,
  cleaningResource,
  documentResource,
  methodResource,
  reminderResource,
  riskResource,
  separationResource,
  supplierResource,
  waterResource,
  type SetupResource,
} from "./resources.js";
import { listSetupItems } from "./setup-api.js";
import { SetupChecklist } from "./setup-checklist.js";
import {
  parseSection,
  type ChecklistStepKey,
  type SetupSectionKey,
} from "./setup-checklist-model.js";
import { UpdateChecks } from "./update-checks.js";
import { useSetupChecklist } from "./use-setup-checklist.js";

interface SectionEntry {
  key: SetupSectionKey;
  label: string;
  resources: readonly SetupResource[];
}

const kitchen: readonly SectionEntry[] = [
  { key: "checklist", label: "Checklist", resources: [] },
  { key: "locations", label: "Business", resources: [] },
  {
    key: "equipment",
    label: "Fridges and freezers",
    resources: [assetResource, areaResource],
  },
  { key: "answers", label: "Unusual readings", resources: [] },
];

const paperwork: readonly SectionEntry[] = [
  { key: "plan", label: "Review your plan", resources: [] },
  { key: "registration", label: "Registration", resources: [] },
  { key: "suppliers", label: "Suppliers", resources: [supplierResource] },
  { key: "cleaning", label: "Cleaning plan", resources: [cleaningResource] },
  { key: "assign", label: "Who does each check", resources: [] },
  { key: "water", label: "Water", resources: [waterResource] },
  {
    key: "risks",
    label: "Nearby risks",
    resources: [riskResource, separationResource],
  },
  { key: "methods", label: "Proven methods", resources: [methodResource] },
  {
    key: "documents",
    label: "Documents",
    resources: [documentResource, reminderResource],
  },
];

const allSections = [...kitchen, ...paperwork];

const sectionSteps: Partial<Record<SetupSectionKey, ChecklistStepKey>> = {
  assign: "assign",
  cleaning: "cleaning",
  equipment: "equipment",
  locations: "business",
  documents: "documents",
  methods: "methods",
  plan: "plan",
  registration: "registration",
  risks: "risks",
  suppliers: "suppliers",
  water: "water",
};

// Once this section's step is done, offer the next unfinished one.
function NextStep({ section }: { section: SetupSectionKey }) {
  const checklist = useSetupChecklist();
  const own = checklist.steps.find(
    (step) => step.key === sectionSteps[section],
  );
  const next = checklist.next;
  if (!checklist.loaded || !own?.done || !next || next.key === own.key)
    return null;
  const className = cn(buttonVariants(), "w-full sm:w-auto");
  const label = (
    <>
      {next.label} <ArrowRight aria-hidden="true" className="size-4" />
    </>
  );
  return (
    <div className="flex justify-end border-t border-stone-200 pt-5">
      {next.target.search ? (
        <Link className={className} search={next.target.search} to="/setup">
          {label}
        </Link>
      ) : (
        <Link className={className} to={next.target.to}>
          {label}
        </Link>
      )}
    </div>
  );
}

function navClass(active: boolean) {
  return cn(
    "flex min-h-10 items-center rounded-lg px-3 text-sm font-semibold transition",
    active
      ? "bg-emerald-800 text-white"
      : "text-stone-700 hover:bg-stone-100 hover:text-stone-950",
  );
}

function SectionLink({
  active,
  entry,
}: {
  active: boolean;
  entry: SectionEntry;
}) {
  return (
    <Link
      aria-current={active ? "page" : undefined}
      className={navClass(active)}
      search={{ section: entry.key }}
      to="/setup"
    >
      {entry.label}
    </Link>
  );
}

function SubNav({ active }: { active: SetupSectionKey }) {
  const highlighted = active;
  const paperworkActive = paperwork.some((entry) => entry.key === active);
  return (
    <nav aria-label="Setup" className="hidden lg:block">
      <div className="space-y-0.5">
        {kitchen.map((entry) => (
          <SectionLink
            active={entry.key === highlighted}
            entry={entry}
            key={entry.key}
          />
        ))}
      </div>
      <Disclosure
        className="mt-4"
        defaultOpen={paperworkActive}
        key={paperworkActive ? "open" : "closed"}
        summary="Before your verification"
      >
        <div className="space-y-0.5">
          {paperwork.map((entry) => (
            <SectionLink
              active={entry.key === highlighted}
              entry={entry}
              key={entry.key}
            />
          ))}
        </div>
      </Disclosure>
    </nav>
  );
}

function PhoneNav({ active }: { active: SetupSectionKey }) {
  const navigate = useNavigate();
  const highlighted = active;
  return (
    <div className="lg:hidden">
      <Select
        aria-label="Setup"
        onChange={(event) => {
          void navigate({
            search: { section: event.target.value },
            to: "/setup",
          });
        }}
        value={highlighted}
      >
        <optgroup label="Setup">
          {kitchen.map((entry) => (
            <option key={entry.key} value={entry.key}>
              {entry.label}
            </option>
          ))}
        </optgroup>
        <optgroup label="Before your verification">
          {paperwork.map((entry) => (
            <option key={entry.key} value={entry.key}>
              {entry.label}
            </option>
          ))}
        </optgroup>
      </Select>
    </div>
  );
}

export function SetupWorkspacePage() {
  const shell = useShell();
  const search = useSearch({ strict: false }) as {
    add?: unknown;
    section?: unknown;
  };
  const section = parseSection(search.section);
  const canManage = shell.activeRole !== "staff";
  const locationId = shell.activeLocation?.id;
  const areas = useQuery({
    enabled: Boolean(locationId && canManage),
    queryFn: () => listSetupItems(areaResource, locationId!),
    queryKey: ["setup", "areas", locationId],
  });

  if (!canManage) return <Navigate replace to="/" />;

  const current = allSections.find((entry) => entry.key === section);
  const needsLocation =
    section !== "checklist" && section !== "locations" && section !== "answers";

  return (
    <>
      <PageHeader className="mb-6" title="Setup" />
      <div className="grid gap-6 lg:grid-cols-[220px_minmax(0,1fr)]">
        <SubNav active={section} />
        <PhoneNav active={section} />
        <div className="min-w-0 space-y-5">
          {locationId ? <UpdateChecks locationId={locationId} /> : null}
          {section === "checklist" ? <SetupChecklist /> : null}
          {section === "locations" ? (
            <LocationPanel autoAdd={search.add === "1"} canManage />
          ) : null}
          {section === "answers" ? (
            <>
              <h2 className="text-lg font-semibold text-stone-950">
                Unusual readings
              </h2>
              <RuleAnswersPanel />
            </>
          ) : null}
          {section === "assign" ? (
            <>
              <h2 className="text-lg font-semibold text-stone-950">
                Who does each check
              </h2>
              {locationId ? <AssignChecks locationId={locationId} /> : null}
            </>
          ) : null}
          {needsLocation && !locationId ? (
            <EmptyState
              action={
                <Link
                  className={buttonVariants()}
                  search={{ section: "locations" }}
                  to="/setup"
                >
                  Add your first location
                </Link>
              }
              title="No location yet"
            />
          ) : null}
          {section === "plan" && locationId ? (
            <PlanWorkspace canManage locationId={locationId} />
          ) : null}
          {section === "registration" && locationId ? (
            <RegistrationPanel locationId={locationId} />
          ) : null}
          {current && current.resources.length > 0 && locationId
            ? current.resources.map((resource) =>
                resource.key === areaResource.key ? (
                  <Disclosure
                    defaultOpen={(areas.data?.length ?? 0) > 0}
                    key={resource.key}
                    summary="Areas"
                  >
                    <ResourcePanel
                      canManage
                      locationId={locationId}
                      resource={resource}
                    />
                  </Disclosure>
                ) : (
                  <ResourcePanel
                    areaOptions={(areas.data ?? []).map((area) => ({
                      label: typeof area.name === "string" ? area.name : "Area",
                      value: area.id,
                    }))}
                    canManage
                    key={resource.key}
                    locationId={locationId}
                    resource={resource}
                  />
                ),
              )
            : null}
          {locationId || section === "locations" ? (
            <NextStep section={section} />
          ) : null}
        </div>
      </div>
    </>
  );
}
